import React, { useState, useEffect } from 'react';
import { Trophy, Clock, Award, Info, ChevronDown, ChevronUp, Play, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { BACKEND_URL } from '../services/api';
import axios from '../services/api';

const WeeklyCountdown = () => {
    const [weekData, setWeekData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [timeLeft, setTimeLeft] = useState(0);
    const [showInfo, setShowInfo] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);

    useEffect(() => {
        fetchWeekData();
        const interval = setInterval(fetchWeekData, 30000); // 30s refresh is enough
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!weekData) return;
        setTimeLeft(weekData.time_remaining_seconds);
        const timer = setInterval(() => {
            setTimeLeft(prev => Math.max(0, prev - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [weekData]);

    const fetchWeekData = async () => {
        try {
            const response = await axios.get('/play-of-week/current');
            setWeekData(response.data);
        } catch (error) {
            console.error('Failed to fetch week data:', error);
        } finally {
            setLoading(false);
        }
    };

    const TimeBlock = ({ value, label, color = "text-white" }) => (
        <div className="flex flex-col items-center min-w-[32px]">
            <span className={`${color} font-bold text-xl md:text-2xl font-mono leading-none tabular-nums`}>
                {String(value).padStart(2, '0')}
            </span>
            <span className="text-[8px] uppercase font-black tracking-tighter text-gray-500 mt-0.5">
                {label}
            </span>
        </div>
    );

    const renderTimer = (seconds) => {
        const d = Math.floor(seconds / 86400);
        const h = Math.floor((seconds % 86400) / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;

        return (
            <div className="flex items-center gap-1 md:gap-2">
                {d > 0 && (
                    <>
                        <TimeBlock value={d} label="GÜN" />
                        <span className="text-gray-700 font-bold self-start mt-1">:</span>
                    </>
                )}
                <TimeBlock value={h} label="SAAT" />
                <span className="text-gray-700 font-bold self-start mt-1">:</span>
                <TimeBlock value={m} label="DAK" color="text-green-400" />
            </div>
        );
    };

    if (loading) return <div className="mb-6 h-28 bg-[#09090b] rounded-2xl animate-pulse border border-gray-800" />;
    if (!weekData || weekData.status !== 'active') return null;

    return (
        <Card className="mb-6 bg-gradient-to-b from-[#111113] to-[#09090b] border-gray-800 shadow-2xl overflow-hidden rounded-2xl">
            {/* Header Section */}
            <div className="p-4 md:p-5 relative overflow-hidden">
                {/* Background Decor */}
                <div className="absolute top-0 left-0 w-full h-full bg-purple-600/5 pointer-events-none" />

                {/* Gradient Accents */}
                <div className="absolute -top-12 -left-12 w-32 h-32 bg-purple-600/10 blur-[60px] rounded-full" />
                <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-green-600/10 blur-[60px] rounded-full" />

                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 md:gap-5 relative z-10">
                    {/* Left: Info */}
                    <div className="flex items-center gap-4 w-full sm:w-auto">
                        <div className="bg-gradient-to-br from-[#1a1a1c] to-[#121214] p-3 rounded-2xl border border-white/5 shadow-xl group transition-all duration-300">
                            <Trophy className="h-6 w-6 md:h-7 md:w-7 text-yellow-500 group-hover:scale-110 transition-transform" />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                                <h3 className="text-white font-black text-lg md:text-xl tracking-tight uppercase leading-none">Haftanın Oyunu</h3>
                                <div
                                    onMouseEnter={() => setShowInfo(true)}
                                    onMouseLeave={() => setShowInfo(false)}
                                    className="text-gray-600 hover:text-purple-400 transition-colors cursor-help p-1"
                                >
                                    <Info className="h-3.5 w-3.5" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="bg-purple-600 shadow-[0_0_10px_rgba(147,51,234,0.5)] text-white text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-tighter">Live</span>
                                <p className="text-gray-500 text-[9px] md:text-[10px] font-bold uppercase tracking-[0.15em]">Liderlik Tablosu</p>
                            </div>
                        </div>
                    </div>

                    {/* Right: Timer Section */}
                    <div className="flex items-center gap-4 md:gap-6 bg-white/[0.04] backdrop-blur-md rounded-2xl px-4 py-2.5 md:px-5 md:py-3 border border-white/5 w-full sm:w-auto justify-between sm:justify-end">
                        <div className="pr-3 md:pr-4 border-r border-white/5">
                            <p className="text-gray-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-left md:text-right leading-none mb-1">Kalan</p>
                            <p className="text-gray-500 text-[9px] md:text-[10px] font-black uppercase tracking-widest text-left md:text-right leading-none">Süre</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="bg-green-500/10 p-1.5 rounded-lg border border-green-500/20 hidden xs:block">
                                <Clock className="h-3.5 w-3.5 text-green-400" />
                            </div>
                            {renderTimer(timeLeft)}
                        </div>
                    </div>
                </div>

                {/* Info Popover */}
                {showInfo && (
                    <div className="absolute top-20 left-4 md:left-20 z-50 bg-[#1c1c1e] border border-white/10 p-4 rounded-2xl shadow-2xl w-72 animate-in fade-in zoom-in duration-200 backdrop-blur-xl">
                        <div className="text-sm text-gray-300 leading-relaxed">
                            <p className="font-bold text-white mb-2 flex items-center gap-2 uppercase text-xs">
                                <Trophy className="h-3 w-3 text-yellow-500" /> Haftanın Oyunu Nedir?
                            </p>
                            Haftalık olarak düzenlenen bu yarışmada, paylaşılan highlight videoları arasından en çok oy alan video haftanın kazananı seçilir ve profilinde özel bir rozet sergileme hakkı kazanır.
                        </div>
                        <div className="absolute -top-1.5 left-6 w-3 h-3 bg-[#1c1c1e] border-t border-l border-white/10 transform rotate-45" />
                    </div>
                )}
            </div>

            {/* Expand Toggle */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full h-8 flex items-center justify-center bg-white/[0.02] hover:bg-white/[0.05] border-t border-white/5 transition-all group"
            >
                <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-gray-500 group-hover:text-purple-400 transition-colors uppercase tracking-widest">
                        {isExpanded ? 'Detayları Gizle' : 'Güncel Sıralamayı Gör'}
                    </span>
                    {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-gray-600 group-hover:text-purple-400" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-gray-600 group-hover:text-purple-400" />
                    )}
                </div>
            </button>

            {/* Expandable Leaderboard */}
            {isExpanded && (
                <div className="border-t border-white/5 p-4 md:p-6 bg-gradient-to-b from-transparent to-purple-600/5 animate-in slide-in-from-top-4 duration-300">
                    <CardContent className="p-0">
                        {weekData.current_leader ? (
                            <div className="space-y-6">
                                {/* Current Leader Spotlight */}
                                <div className="relative group">
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-green-600 rounded-2xl blur opacity-20 group-hover:opacity-40 transition duration-500" />
                                    <div className="relative flex items-center justify-between bg-[#1a1a1c] rounded-2xl p-4 border border-white/5 transition-all duration-300">
                                        <div className="flex items-center gap-4">
                                            <div className="relative group/avatar cursor-pointer" onClick={() => setSelectedVideo(weekData.current_leader.video)}>
                                                <div className="absolute inset-0 bg-yellow-400 blur-md opacity-20" />
                                                <Avatar className="h-14 w-14 border-2 border-yellow-500/50 shadow-2xl relative">
                                                    <AvatarImage src={weekData.current_leader.user.avatar_url ? `${BACKEND_URL}${weekData.current_leader.user.avatar_url}` : null} className="object-cover" />
                                                    <AvatarFallback className="bg-purple-950 text-purple-200 font-black text-lg">
                                                        {weekData.current_leader.user.username?.substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover/avatar:opacity-100 transition-opacity">
                                                    <Play className="h-6 w-6 text-white fill-white" />
                                                </div>
                                                <div className="absolute -bottom-2 -right-2 bg-yellow-500 text-black text-[10px] w-6 h-6 flex items-center justify-center rounded-full font-black shadow-lg border-2 border-[#1a1a1c]">1</div>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <p className="text-white font-black text-lg tracking-tight">{weekData.current_leader.user.username}</p>
                                                    <Award className="h-4 w-4 text-yellow-500 fill-yellow-500 animate-pulse" />
                                                </div>
                                                <p className="text-gray-500 text-xs font-medium italic mt-0.5">&quot;{weekData.current_leader.caption || 'Haftanın Favorisi'}&quot;</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <div className="bg-yellow-500/10 px-3 py-1.5 rounded-xl border border-yellow-500/20 flex items-center gap-2">
                                                <Trophy className="h-4 w-4 text-yellow-500" />
                                                <span className="text-white font-black text-xl tabular-nums">{weekData.current_leader.vote_count}</span>
                                            </div>
                                            <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest mt-1 mr-1">Oy Sayısı</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Top 3 Rankings */}
                                {weekData.top_3 && weekData.top_3.length > 1 && (
                                    <div className="pt-2">
                                        <h4 className="text-gray-500 text-[10px] font-black uppercase tracking-[0.2em] mb-4 pl-1">Üst Sıralama</h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                            {weekData.top_3.map((post, idx) => (
                                                <div key={post.post_id} className={`flex items-center justify-between p-3 rounded-xl border transition-all duration-300 ${idx === 0 ? 'bg-purple-600/10 border-purple-500/30 ring-1 ring-purple-500/20' : 'bg-[#121214] border-white/5'}`}>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-xs font-black w-3 ${idx === 0 ? 'text-purple-400' : 'text-gray-600'}`}>#{idx + 1}</span>
                                                        <div
                                                            className="relative group/mini cursor-pointer"
                                                            onClick={() => setSelectedVideo(post.video)}
                                                        >
                                                            <Avatar className="h-8 w-8 border border-white/10">
                                                                <AvatarImage src={post.user.avatar_url ? `${BACKEND_URL}${post.user.avatar_url}` : null} />
                                                                <AvatarFallback className="text-[10px]">{post.user.username?.substring(0, 2).toUpperCase()}</AvatarFallback>
                                                            </Avatar>
                                                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover/mini:opacity-100 transition-opacity">
                                                                <Play className="h-3 w-3 text-white fill-white" />
                                                            </div>
                                                        </div>
                                                        <span className="text-white text-xs font-bold truncate max-w-[80px]">{post.user.username}</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-gray-400 font-bold text-xs">
                                                        <Trophy className="h-3 w-3 text-yellow-600" />
                                                        <span className="tabular-nums">{post.vote_count}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-12 text-center">
                                <Trophy className="h-10 w-10 text-gray-800 mx-auto mb-3 opacity-20" />
                                <p className="text-gray-500 text-sm font-medium">Henüz bir video paylaşılmadı. Lider olmak için hemen paylaş!</p>
                            </div>
                        )}
                    </CardContent>
                </div>
            )}

            {/* Video Playback Modal */}
            {selectedVideo && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="relative w-full max-w-4xl aspect-video bg-[#09090b] rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
                        <button
                            onClick={() => setSelectedVideo(null)}
                            className="absolute top-6 right-6 z-[110] bg-white/10 hover:bg-white/20 backdrop-blur-xl text-white p-3 rounded-full transition-all border border-white/10 shadow-2xl active:scale-90"
                        >
                            <X className="h-7 w-7" />
                        </button>
                        <video
                            src={`${BACKEND_URL}${selectedVideo.video_url}`}
                            controls
                            autoPlay
                            className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
                            <h4 className="text-white font-bold text-xl">{selectedVideo.title || 'Haftanın Favorisi'}</h4>
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
};

export default WeeklyCountdown;
